import React, { useState, useEffect, useMemo, useContext } from 'react'
import { interceptExternalLinkClick } from '../lib/nativeShell'
import { useParams, useNavigate } from 'react-router-dom'
import { Plus, Edit2, Trash2, Search, X, FileText, Lock } from 'lucide-react'
import { collectionsApi } from '../services/collectionsApi'
import { renderCollectionItem, getVisibleFieldNames } from '../utils/collectionUtils'
import CreateCollectionItemModal from '../components/collections/CreateCollectionItemModal'
import EditCollectionItemModal from '../components/collections/EditCollectionItemModal'
import EditCollectionModal from '../components/collections/EditCollectionModal'
import MergeSelectionBar from '../components/collections/MergeSelectionBar'
import MergeReviewModal from '../components/collections/MergeReviewModal'
import { formatMergeSummary, formatPruneSummary } from '../components/collections/cleanupSummaries'
import { useNotify } from '../components/notice/useNotify'
import MergeCheckbox from '../components/collections/MergeCheckbox'
import CollectionItemPeekModal from '../components/collections/CollectionItemPeekModal'
import { useMergeSelection } from '../hooks/useMergeSelection'
import { useCollectionHealth } from '../hooks/useCollectionHealth'
import CollectionActionsMenu from '../components/collections/CollectionActionsMenu'
import RoundPlusButton from '../components/RoundPlusButton'
import DuplicateReview from '../components/collections/DuplicateReview'
import PlacesCollectionPage from './PlacesCollectionPage'
import PeopleCollectionPage from './PeopleCollectionPage'
import Spinner from '../components/Spinner'
import { UserContext } from '../components/UserContext'
import type { CollectionWithItems, CollectionItem } from '../types/collections'

const CollectionDetailPage: React.FC = () => {
  const { collectionId } = useParams<{ collectionId: string }>()
  const navigate = useNavigate()
  const { user } = useContext(UserContext)
  const [collection, setCollection] = useState<CollectionWithItems | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isCreateItemModalOpen, setIsCreateItemModalOpen] = useState(false)
  const [editingItem, setEditingItem] = useState<CollectionItem | null>(null)
  const [isEditingCollection, setIsEditingCollection] = useState(false)
  const [deletingItemId, setDeletingItemId] = useState<number | null>(null)
  const [deleteModalState, setDeleteModalState] = useState<{
    item: CollectionItem | null
    referenceCount?: number
    checkingReferences?: boolean
  }>({ item: null })
  const [filterText, setFilterText] = useState('')
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set())
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null)

  // Entity-merge (de-duplication) multi-select for generic collections.
  // Selection survives the search filter and opening/closing the peek. Stage 6
  // wires `handleMergeClick` to the review modal.
  const merge = useMergeSelection()
  const [mergeReviewIds, setMergeReviewIds] = useState<number[] | null>(null)
  const notify = useNotify()
  const handleMergeClick = () => {
    setMergeReviewIds([...merge.selected])
  }
  // Prune-unreferenced multi-select (sibling of merge; uncapped, min 1). Entering
  // prune mode lists only zero-live-reference items, all pre-checked for deletion.
  // Merge and prune are mutually exclusive — entering one exits the other.
  const prune = useMergeSelection({ min: 1, max: Infinity })
  // Counts for the two buttons below, so the page says whether either
  // would find anything before it is pressed. Refreshed after a prune or
  // a merge; a rename that makes or breaks a collision shows next load.
  // `collection` is null until the fetch lands, and hooks run before this
  // component's loading guard returns — hence the optional chain.
  const { health, refresh: refreshHealth } = useCollectionHealth(collection?.id ?? null)
  // The grouped review behind the collision badge. Its own mode, exclusive
  // with merge and prune the way those two already are with each other.
  const [reviewingDuplicates, setReviewingDuplicates] = useState(false)
  const [pruneCandidateIds, setPruneCandidateIds] = useState<Set<number> | null>(null)
  const [pruneConfirmOpen, setPruneConfirmOpen] = useState(false)
  const [pruning, setPruning] = useState(false)
  const selectMode = merge.mergeMode || prune.mergeMode
  const activeSelection = prune.mergeMode ? prune : merge
  // Non-destructive peek: which item's check-ins are being previewed (reuses
  // the single-item checkins fetch; never navigates away or mutates selection).
  const [peekItem, setPeekItem] = useState<CollectionItem | null>(null)

  // Handle Esc key for fullscreen image
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && fullscreenImage) {
        setFullscreenImage(null)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [fullscreenImage])

  useEffect(() => {
    const fetchCollection = async () => {
      if (!collectionId) return
      
      try {
        setLoading(true)
        const data = await collectionsApi.get(parseInt(collectionId), true)
        setCollection(data)
        setError(null)
      } catch (err) {
        setError('Failed to load collection')
        console.error('Error fetching collection:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchCollection()
  }, [collectionId])

  const handleDeleteItem = async (item: CollectionItem) => {
    if (!collection) return

    // First check for references
    setDeleteModalState({ item, checkingReferences: true })

    try {
      const result = await collectionsApi.items.delete(collection.id, item.id)
      console.log('Delete check result:', result)

      if (result.can_delete === false) {
        // Has references, update modal with reference info
        setDeleteModalState({
          item,
          referenceCount: result.reference_count || 0,
          checkingReferences: false
        })
      } else {
        // No references, show simple confirmation
        setDeleteModalState({
          item,
          checkingReferences: false
        })
      }
    } catch (err) {
      console.error('Error checking references:', err)
      // On error, show simple confirmation
      setDeleteModalState({
        item,
        checkingReferences: false
      })
    }
  }

  const handleConfirmDelete = async () => {
    if (!collection || !deleteModalState.item) return

    setDeletingItemId(deleteModalState.item.id)
    try {
      // The first call (no force) is a pure probe; the actual deletion always
      // requires force=true regardless of whether references exist.
      await collectionsApi.items.delete(collection.id, deleteModalState.item.id, true)

      // Refresh collection data
      const refreshedData = await collectionsApi.get(collection.id, true)
      setCollection(refreshedData)
      setDeleteModalState({ item: null })
    } catch (err) {
      console.error('Error deleting item:', err)
      // A notice, not the page's `error`: that one replaces the whole page
      // with an error screen, which a failed delete of one item shouldn't.
      notify.error('Failed to delete item')
    } finally {
      setDeletingItemId(null)
    }
  }

  const handleItemCreated = async () => {
    if (!collection) return
    const refreshedData = await collectionsApi.get(collection.id, true)
    setCollection(refreshedData)
    setIsCreateItemModalOpen(false)
  }

  const handleItemUpdated = async () => {
    if (!collection) return
    const refreshedData = await collectionsApi.get(collection.id, true)
    setCollection(refreshedData)
    setEditingItem(null)
  }

  const handleCollectionUpdated = async () => {
    if (!collection) return
    const refreshedData = await collectionsApi.get(collection.id, true)
    setCollection(refreshedData)
    setIsEditingCollection(false)
  }

  // --- Prune mode wiring -------------------------------------------------
  const exitPrune = () => {
    prune.exitMergeMode()
    setPruneCandidateIds(null)
  }
  const enterPrune = async () => {
    if (!collection) return
    merge.exitMergeMode()
    const ids = collection.items.map((i) => i.id)
    try {
      const counts = await collectionsApi.items.referenceCounts(collection.id, ids)
      const candidates = ids.filter((id) => (counts[id] ?? 0) === 0)
      setPruneCandidateIds(new Set(candidates))
      prune.selectAll(candidates)
      prune.enterMergeMode()
    } catch (err) {
      console.error('Failed to load reference counts for prune:', err)
    }
  }
  const toggleMerge = () => {
      if (merge.mergeMode) {
          merge.exitMergeMode()
          return
      }
      if (reviewingDuplicates) {
          setReviewingDuplicates(false)
          return
      }
      exitPrune()
      // When grouping has already found collisions, showing them beats opening
      // an empty multi-select and asking the user to find them by eye. With
      // none found there is nothing to show, so fall through to picking.
      //
      // Dismissed clusters count as something to show even when no LIVE one
      // is left: the review panel is the only route to Restore, so gating on
      // active clusters alone strands a collection that has ruled on all of
      // them — the misclick this was all supposed to stay recoverable from.
      if ((health?.duplicate_clusters.length ?? 0) > 0
          || (health?.dismissed_clusters.length ?? 0) > 0) {
          setReviewingDuplicates(true)
          return
      }
      merge.enterMergeMode()
  }
  const togglePrune = () => {
    if (prune.mergeMode) {
      exitPrune()
      return
    }
    // Third member of the same mutually-exclusive set.
    setReviewingDuplicates(false)
    void enterPrune()
  }
  const handlePruneConfirm = async () => {
    if (!collection) return
    setPruning(true)
    try {
      const result = await collectionsApi.items.purgeUnreferenced(collection.id, [...prune.selected])
      exitPrune()
      setPruneConfirmOpen(false)
      const refreshed = await collectionsApi.get(collection.id, true)
      setCollection(refreshed)
      notify.success(formatPruneSummary(result.reaped_ids.length, result.skipped_ids.length))
    } catch (err) {
      console.error('Failed to purge unreferenced items:', err)
      void refreshHealth()
    } finally {
      setPruning(false)
    }
  }

  // Filter items based on search text (memoized to prevent re-computation on every render).
  // In prune mode the list is further narrowed to the zero-reference candidate set.
  const filteredItems = useMemo(() => {
    if (!collection?.items) return []

    let base = collection.items
    if (filterText.trim()) {
      const searchLower = filterText.toLowerCase()
      base = base.filter(item => {
        return Object.values(item.data).some(value => {
          if (value === null || value === undefined) return false
          return String(value).toLowerCase().includes(searchLower)
        })
      })
    }
    if (prune.mergeMode && pruneCandidateIds) {
      base = base.filter((item) => pruneCandidateIds.has(item.id))
    }
    return base
  }, [collection?.items, filterText, prune.mergeMode, pruneCandidateIds])

  // Memoize field names computation. System collections (e.g. Places) are
  // filtered through a whitelist in `getVisibleFieldNames` so internal
  // columns like `google_place_id` and `source` don't surface in the UI.
  const fieldNames = useMemo(
    () => (collection ? getVisibleFieldNames(collection) : []),
    [collection]
  )

  const isSystem = !!collection?.is_system

  const renderFieldValue = (value: any, fieldType: string) => {
    if (value === null || value === undefined) {
      return <span className="text-gray-400">—</span>
    }

    switch (fieldType) {
      case 'boolean':
        return value ? '✓' : '✗'
      case 'url':
        return (
          <a href={value} target="_blank" rel="noopener noreferrer" onClick={interceptExternalLinkClick} className="text-amber-800 hover:underline break-all">
            {value}
          </a>
        )
      case 'email':
        return (
          <a href={`mailto:${value}`} className="text-amber-800 hover:underline break-all">
            {value}
          </a>
        )
      case 'picture':
        return (
          <img 
            src={value} 
            alt="Item" 
            className="w-12 h-12 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => setFullscreenImage(value)}
          />
        )
      default:
        return <span>{String(value)}</span>
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    )
  }

  if (error || !collection) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-rose-500">{error || 'Collection not found'}</div>
      </div>
    )
  }

  // Custom view for the Places system collection. The generic table is
  // unhelpful for places (lat/lng numbers, no map), so we hand off to a
  // map+cards page. Other system collections (and all user-created ones)
  // continue to use the generic flow below.
  if (collection.system_kind === 'places') {
    return (
      <PlacesCollectionPage
        collection={collection}
        isAdmin={!!user?.isAdmin}
        onCollectionRefresh={async () => {
          const refreshed = await collectionsApi.get(collection.id, true)
          setCollection(refreshed)
        }}
      />
    )
  }

  if (collection.system_kind === 'people') {
    return (
      <PeopleCollectionPage
        collection={collection}
        isAdmin={!!user?.isAdmin}
        onCollectionRefresh={async () => {
          const refreshed = await collectionsApi.get(collection.id, true)
          setCollection(refreshed)
        }}
      />
    )
  }

  return (
    <div className="min-h-screen bg-blush-light">
      <div className="max-w-7xl mx-auto px-4 pb-8 sm:pt-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 truncate max-w-full">{collection.name}</h1>
                {isSystem && (
                  <span
                    className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded"
                    title="The structure of this collection is fixed, but you can add, edit, and remove items the same as any other collection. Honeyguide also adds items here automatically as you tag locations on checkins."
                  >
                    <Lock className="w-3 h-3" />
                    Fixed structure
                  </span>
                )}
              </div>
              {collection.description && (
                <p className="text-gray-600 mt-1">{collection.description}</p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {/* Edit changes the schema, which system collections fix; admins
                  still get it to inspect one (the backend rejects the write). */}
              <CollectionActionsMenu
                onEdit={!isSystem || user?.isAdmin ? () => setIsEditingCollection(true) : undefined}
                merge={collection.items.length > 0 ? {
                  active: merge.mergeMode || reviewingDuplicates,
                  count: health?.duplicate_clusters.length,
                  onToggle: toggleMerge,
                } : undefined}
                prune={collection.items.length > 0 ? {
                  active: prune.mergeMode,
                  count: health?.orphan_count,
                  onToggle: togglePrune,
                } : undefined}
              />
              <RoundPlusButton onClick={() => setIsCreateItemModalOpen(true)} label="Add item" />
            </div>
          </div>
        </div>

        {/* Items */}
        {collection.items.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <p className="text-gray-500 mb-4">
              {isSystem
                ? 'No items yet. This collection is populated automatically as you tag locations on checkins, but you can also add your own.'
                : 'No items in this collection yet.'}
            </p>
            <button
              onClick={() => setIsCreateItemModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-action text-plum rounded-lg hover:bg-action-dark transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add First Item
            </button>
          </div>
        ) : (
          <>
            {reviewingDuplicates && health && (
              <DuplicateReview
                collectionId={collection.id}
                clusters={health.duplicate_clusters}
                dismissedClusters={health.dismissed_clusters}
                items={collection.items}
                onMerge={(ids) => setMergeReviewIds(ids)}
                onChanged={refreshHealth}
                onManualSelect={() => {
                  setReviewingDuplicates(false)
                  merge.enterMergeMode()
                }}
                onOpenDetail={(id) => {
                  const hit = collection.items.find((it) => it.id === id)
                  if (hit) setPeekItem(hit)
                }}
                onDone={() => setReviewingDuplicates(false)}
              />
            )}

            {/* Desktop Table View */}
            <div className="hidden lg:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              {/* Search bar */}
              {collection.items.length > 0 && (
                <div className="bg-gray-50 border-b border-gray-200 p-3 flex items-center gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search items..."
                      value={filterText}
                      onChange={(e) => setFilterText(e.target.value)}
                      className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-action bg-white"
                    />
                  </div>
                  {filterText && (
                    <p className="text-xs text-gray-500 flex-shrink-0">
                      {filteredItems.length} of {collection.items.length}
                    </p>
                  )}
                </div>
              )}
              
              {/* Scrollable container with fixed height */}
              <div className="overflow-auto relative" style={{ maxHeight: '65vh' }}>
                <table className="w-full relative">
                  <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-20">
                    <tr>
                      {selectMode && (
                        <th className="px-3 py-3 w-10 bg-gray-50" />
                      )}
                      {fieldNames.map((fieldName, index) => (
                        <th
                          key={fieldName}
                          className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 ${
                            index === 0 && !selectMode ? 'sticky left-0 z-30 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]' : ''
                          }`}
                        >
                          <div className="truncate max-w-32" title={fieldName}>
                            {fieldName}
                            {collection.config.fields[fieldName].required && (
                              <span className="text-rose-500 ml-1">*</span>
                            )}
                          </div>
                        </th>
                      ))}
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-20 bg-gray-50">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredItems.length === 0 ? (
                      <tr>
                        <td colSpan={fieldNames.length + 1 + (selectMode ? 1 : 0)} className="px-4 py-12 text-center text-gray-500">
                          {prune.mergeMode ? 'No unreferenced items to prune.' : 'No items match your search.'}
                        </td>
                      </tr>
                    ) : (
                      filteredItems.map((item) => (
                      <tr
                        key={item.id}
                        className={`hover:bg-gray-50 group ${
                          selectMode ? 'cursor-pointer' : ''
                        } ${selectMode && activeSelection.isSelected(item.id) ? 'bg-amber-50' : ''}`}
                        onClick={selectMode ? () => setPeekItem(item) : undefined}
                      >
                        {selectMode && (
                          <td className="px-3 py-3">
                            <MergeCheckbox
                              checked={activeSelection.isSelected(item.id)}
                              onToggle={() => activeSelection.toggle(item.id)}
                            />
                          </td>
                        )}
                        {fieldNames.map((fieldName, index) => (
                          <td
                            key={fieldName}
                            className={`px-4 py-3 text-sm text-gray-900 ${
                              index === 0 && !selectMode ? 'sticky left-0 z-10 bg-white group-hover:bg-gray-50 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]' : ''
                            }`}
                          >
                            <div className="max-w-32">
                              {renderFieldValue(
                                item.data[fieldName],
                                collection.config.fields[fieldName].type
                              )}
                            </div>
                          </td>
                        ))}
                        <td className="px-4 py-3 text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end gap-1">
                            <button
                              onClick={() => navigate(`/checkins?collection_item=${item.id}`)}
                              className="p-1 text-gray-600 hover:text-green-600 transition-colors cursor-pointer"
                              title="View checkins with this item"
                            >
                              <FileText className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setEditingItem(item)}
                              className="p-1 text-gray-600 hover:text-amber-900 transition-colors cursor-pointer"
                              title="Edit item"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteItem(item)}
                              disabled={deletingItemId === item.id}
                              className="p-1 text-gray-600 hover:text-rose-600 transition-colors disabled:opacity-50 cursor-pointer"
                              title="Delete item"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden">
              {/* Search bar for mobile */}
              {collection.items.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 mb-4 flex items-center gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search items..."
                      value={filterText}
                      onChange={(e) => setFilterText(e.target.value)}
                      className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-action"
                    />
                  </div>
                  {filterText && (
                    <p className="text-xs text-gray-500 flex-shrink-0">
                      {filteredItems.length} of {collection.items.length}
                    </p>
                  )}
                </div>
              )}
              
              <div className="space-y-4">
              {filteredItems.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                  <p className="text-gray-500">{prune.mergeMode ? 'No unreferenced items to prune.' : 'No items match your search.'}</p>
                </div>
              ) : (
                filteredItems.map((item) => (
                <div
                  key={item.id}
                  className={`bg-white rounded-lg shadow-sm border p-4 ${
                    selectMode ? 'cursor-pointer' : ''
                  } ${
                    selectMode && activeSelection.isSelected(item.id)
                      ? 'border-amber-500 ring-2 ring-action'
                      : 'border-gray-200'
                  }`}
                  onClick={selectMode ? () => setPeekItem(item) : undefined}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      {selectMode && (
                        <div className="flex-shrink-0 pt-0.5">
                          <MergeCheckbox
                            checked={activeSelection.isSelected(item.id)}
                            onToggle={() => activeSelection.toggle(item.id)}
                          />
                        </div>
                      )}
                      {(() => {
                        // Find picture fields
                        const pictureFields = fieldNames.filter(fieldName => {
                          const field = collection.config.fields[fieldName]
                          const value = item.data[fieldName]
                          return field.type === 'picture' && value !== null && value !== undefined
                        })
                        
                        return (
                          <>
                            {pictureFields.map(fieldName => (
                              <img 
                                key={fieldName}
                                src={item.data[fieldName]} 
                                alt={fieldName}
                                className="w-12 h-12 object-cover rounded-lg flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => setFullscreenImage(item.data[fieldName])}
                              />
                            ))}
                            <div className="font-medium text-gray-900 truncate flex-1">
                              {renderCollectionItem(item, collection)}
                            </div>
                          </>
                        )
                      })()}
                    </div>
                    <div className="flex gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => navigate(`/checkins?collection_item=${item.id}`)}
                        className="p-1 text-gray-600 hover:text-green-600 transition-colors cursor-pointer"
                        title="View checkins with this item"
                      >
                        <FileText className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setEditingItem(item)}
                        className="p-1 text-gray-600 hover:text-amber-900 transition-colors cursor-pointer"
                        title="Edit item"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteItem(item)}
                        disabled={deletingItemId === item.id}
                        className="p-1 text-gray-600 hover:text-rose-600 transition-colors disabled:opacity-50 cursor-pointer"
                        title="Delete item"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {(() => {
                      const isExpanded = expandedItems.has(item.id)
                      
                      // Get non-null fields excluding pictures
                      const nonNullFields = fieldNames.filter(fieldName => {
                        const value = item.data[fieldName]
                        const field = collection.config.fields[fieldName]
                        return value !== null && value !== undefined && field.type !== 'picture'
                      })
                      
                      const fieldsToShow = isExpanded ? nonNullFields : nonNullFields.slice(0, 5)
                      const hasMoreFields = nonNullFields.length > 5
                      
                      return (
                        <>
                          {fieldsToShow.map((fieldName) => {
                            const value = item.data[fieldName]
                            const field = collection.config.fields[fieldName]
                            
                            return (
                              <div key={fieldName} className="flex items-baseline gap-2">
                                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider flex-shrink-0">
                                  {fieldName}:
                                </span>
                                <span className="text-sm text-gray-900 break-words flex-1">
                                  {renderFieldValue(value, field.type)}
                                </span>
                              </div>
                            )
                          })}
                          {hasMoreFields && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                const newExpanded = new Set(expandedItems)
                                if (isExpanded) {
                                  newExpanded.delete(item.id)
                                } else {
                                  newExpanded.add(item.id)
                                }
                                setExpandedItems(newExpanded)
                              }}
                              className="text-sm text-amber-800 hover:text-amber-900 font-medium mt-2"
                            >
                              {isExpanded ? 'See less' : 'See more'}
                            </button>
                          )}
                        </>
                      )
                    })()}
                  </div>
                </div>
                ))
              )}
              </div>
            </div>
          </>
        )}

        {/* Merge selection bar — floats while in merge mode. "Merge" is a
            Stage-6 hand-off; here it only surfaces count + enable state. */}
        <MergeSelectionBar
          visible={merge.mergeMode}
          count={merge.count}
          onMergeClick={handleMergeClick}
          onClear={merge.clear}
          onCancel={merge.exitMergeMode}
        />

        {/* Prune selection bar — the red "Delete N" sibling of the merge bar. */}
        <MergeSelectionBar
          visible={prune.mergeMode}
          count={prune.count}
          variant="prune"
          actionLabel="Delete"
          actionIcon={Trash2}
          minCount={1}
          maxCount={Infinity}
          hintText={{ empty: 'Select items to delete', belowMin: 'Select at least 1' }}
          onMergeClick={() => setPruneConfirmOpen(true)}
          onClear={prune.clear}
          onCancel={exitPrune}
        />

        {/* Non-destructive check-in peek for a merge candidate. Reuses the
            single-item checkins fetch; opening/closing it never touches the
            selection. */}
        <CollectionItemPeekModal
          open={peekItem !== null}
          collectionItemId={peekItem?.id ?? null}
          title={peekItem && collection ? renderCollectionItem(peekItem, collection) : ''}
          onClose={() => setPeekItem(null)}
        />

        {mergeReviewIds && (
          <MergeReviewModal
            collection={collection}
            itemIds={mergeReviewIds}
            onMerged={async (summary) => {
              setMergeReviewIds(null)
              merge.exitMergeMode()
              const refreshed = await collectionsApi.get(collection.id, true)
              setCollection(refreshed)
              void refreshHealth()
              notify.success(
                formatMergeSummary(
                  summary.repointed_checkin_count,
                  summary.reaped_loser_ids.length
                )
              )
            }}
            onClose={() => setMergeReviewIds(null)}
          />
        )}

        {/* Prune confirmation — mirrors the single-item delete modal, but
            deletes the whole still-checked candidate set at once. */}
        {pruneConfirmOpen && (
          <div
            className="fixed inset-0 bg-white/75 z-50 flex items-center justify-center p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget && !pruning) setPruneConfirmOpen(false)
            }}
            style={{ backdropFilter: 'blur(1px)' }}
          >
            <div className="bg-white rounded-lg p-6 w-full max-w-lg shadow-2xl border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Prune unreferenced items</h3>
              <p className="text-gray-700 mb-6">
                Delete {prune.count} unreferenced item{prune.count === 1 ? '' : 's'}? This cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setPruneConfirmOpen(false)}
                  disabled={pruning}
                  className="px-4 py-2 text-gray-700 bg-secondary hover:bg-secondary-dark rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePruneConfirm}
                  disabled={pruning}
                  className="px-4 py-2 bg-danger hover:bg-danger-dark text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {pruning ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Deleting…
                    </>
                  ) : (
                    'Delete'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modals */}
        {isCreateItemModalOpen && (
          <CreateCollectionItemModal
            collection={collection}
            onClose={() => setIsCreateItemModalOpen(false)}
            onSuccess={handleItemCreated}
          />
        )}

        {editingItem && (
          <EditCollectionItemModal
            collection={collection}
            item={editingItem}
            onClose={() => setEditingItem(null)}
            onSuccess={handleItemUpdated}
          />
        )}

        {isEditingCollection && (
          <EditCollectionModal
            collection={collection}
            onClose={() => setIsEditingCollection(false)}
            onSuccess={handleCollectionUpdated}
          />
        )}

        {deleteModalState.item && (
          <div
            className="fixed inset-0 bg-white/75 z-50 flex items-center justify-center p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget && !deletingItemId) {
                setDeleteModalState({ item: null })
              }
            }}
            style={{ backdropFilter: 'blur(1px)' }}
          >
            <div className="bg-white rounded-lg p-6 w-full max-w-lg shadow-2xl border border-gray-200">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Delete Item</h3>
              </div>

              <div className="mb-6">
                {deleteModalState.checkingReferences ? (
                  <div className="flex items-center text-gray-600">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                    Checking for references...
                  </div>
                ) : (
                  <>
                    <p className="text-gray-700 mb-3">
                      Are you sure you want to delete "<span className="font-medium">{collection ? renderCollectionItem(deleteModalState.item, collection) : 'this item'}</span>"?
                    </p>

                    {deleteModalState.referenceCount && deleteModalState.referenceCount > 0 && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                        <p className="text-yellow-800 text-sm">
                          ⚠️ This item is referenced in{' '}
                          <a
                            href={`${window.location.origin}/checkins?collection_item=${deleteModalState.item.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium text-amber-900 hover:text-amber-950 underline"
                          >
                            {deleteModalState.referenceCount} checkin{deleteModalState.referenceCount !== 1 ? 's' : ''}
                          </a>.
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setDeleteModalState({ item: null })}
                  disabled={deletingItemId !== null || deleteModalState.checkingReferences}
                  className="px-4 py-2 text-gray-700 bg-secondary hover:bg-secondary-dark rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={deletingItemId !== null || deleteModalState.checkingReferences}
                  className="px-4 py-2 bg-danger hover:bg-danger-dark text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {deletingItemId ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Deleting...
                    </>
                  ) : deleteModalState.referenceCount && deleteModalState.referenceCount > 0 ? (
                    'Delete Anyway'
                  ) : (
                    'Delete'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Fullscreen Image Modal */}
        {fullscreenImage && (
          <div 
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setFullscreenImage(null)}
          >
            <div className="relative w-full h-full flex items-center justify-center">
              <img 
                src={fullscreenImage} 
                alt="Fullscreen view"
                className="max-w-full max-h-full w-auto h-auto object-contain"
                onClick={(e) => e.stopPropagation()}
              />
              <button
                onClick={() => setFullscreenImage(null)}
                className="absolute top-4 right-4 p-2 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default CollectionDetailPage